import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import localeData from "dayjs/plugin/localeData";
import localizedFormat from "dayjs/plugin/localizedFormat";
import weekOfYear from "dayjs/plugin/weekOfYear";
import isoWeek from "dayjs/plugin/isoWeek";

import {
  Alert,
  Button,
  DatePicker,
  Divider,
  Flex,
  Form,
  message,
  Modal,
  Select,
  Switch,
  Upload,
} from "antd";
import Questions from "./Question";
import AttributesDefinitionModal from "./AttributesDefinition";
import FullPageLoader from "./FullPageLoader";
import AnimatedLoader from "./AnimatedLoader";

import {
  ExclamationCircleFilled,
  ExclamationCircleOutlined,
  QuestionCircleTwoTone,
  UploadOutlined,
} from "@ant-design/icons";

import { DATE_FORMAT, DEFAULT_S3_FILE_BUCKET_URL } from "../constant";
import LANGUAGE_LISTS from "../language";
import { colorPalette } from "../theme";
import lngPreviewImage from "../assets/language-preview.png";
import { useAxios } from "../hooks";

// Register dayjs plugins once at module level
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(localizedFormat);
dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);

// --- Constants ---
const ALLOWED_FILE_TYPES = ["application/pdf"];
const MAX_FILE_SIZE_MB = 5;

// --- Helpers ---
const sortLanguagesEnFirst = (langs) =>
  [...langs].sort((a, b) => {
    if (a.code === "en" || a.value === "en") return -1;
    if (b.code === "en" || b.value === "en") return 1;
    return (a.label ?? a.text ?? "").localeCompare(b.label ?? b.text ?? "");
  });

const isArrayEqual = (arr1, arr2) => {
  if (arr1.length !== arr2.length) return false;
  const sortedA = [...arr1].sort();
  const sortedB = [...arr2].sort();
  return sortedA.every((v, i) => v === sortedB[i]);
};

// --- Styled Components ---
const Container = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 10px;
  padding: 20px 20px 0;

  @media (max-width: 479px) {
    display: block;
  }
`;

const LeftWrapper = styled.div`
  width: 40%;
  overflow: hidden auto;
  margin-bottom: 60px;
  border-right: 1px solid #ccc;
  display: flex;
  align-items: center;

  h3 {
    margin-top: 0;
    font-weight: 500;
    color: rgb(52, 71, 103);
  }

  p {
    margin: 10px 0;
  }

  .ant-segmented {
    background-color: #fff;
  }

  @media (max-width: 479px) {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #ccc;
  }
`;

const Wrapper = styled.div`
  width: 80%;
  margin: 20px auto;
  height: calc(100vh - 245px);

  @media (max-width: 768px) {
    width: 100%;
    padding: 0;
    margin: 0 auto 20px;
  }
`;

const FormItem = styled(Form.Item)`
  margin-bottom: 20px;

  .ant-form-item-label > label {
    font-weight: 600;
    font-size: 16px !important;
  }

  .ant-form-item-explain-error {
    margin-bottom: 8px;
  }

  .ant-select {
    width: 100%;
  }

  .ant-form-item-explain {
    font-size: 12px;
    color: rgba(12, 12, 12, 0.88);
    margin: 10px 0;
  }
`;

const RightWrapper = styled.div`
  width: 60%;
  overflow: hidden auto;
  margin-bottom: 55px;

  .ant-spin {
    width: 45%;
  }

  @media (max-width: 479px) {
    width: 100%;
  }
`;

const QuestionContainer = styled.div`
  margin: 0 0 30px;
  height: calc(100vh - 245px);
  overflow: auto none;
`;

const FooterWrapper = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px 20px;
  background: #fff;
  border-top: 1px solid #eee;
  z-index: 10;
`;

const NextButton = styled(Button)`
  width: 25%;

  @media (max-width: 479px) {
    width: 100%;
  }
`;

const SurveyTitle = styled.h2`
  font-size: 28px;
  color: rgb(52, 71, 103);
`;

const ModalContentTile = styled.p`
  border-left: 5px solid ${colorPalette.primary[500]};
  padding-left: 10px;
`;

const SwitchWrapper = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
`;

const PreviewModal = styled(Modal)`
  .ant-modal-body {
    text-align: center;
  }
`;

// --- Component ---
const SurveyPreview = ({ onboarding, onChangeStep, onPreviousStep }) => {
  const [form] = Form.useForm();
  const [modal, contextHolder] = Modal.useModal();

  const { fetch: onSubmit, loading: submitLoading } = useAxios();
  const { fetch: onFetch, loading } = useAxios();
  const { fetch: onGenerateQuestion, loading: questionsLoading } = useAxios();
  const { fetch: onReGenerateQuestion } = useAxios();
  const { fetch: onUpdateQuestion, loading: updateLoading } = useAxios();

  const [error, setError] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [regenerateId, setRegenerateId] = useState(null);
  const [attributes, setAttributes] = useState([]);
  const [policyFile, setPolicyFile] = useState(null);
  const [showAttributesModal, setShowAttributesModal] = useState(false);
  const [questionResponse, setQuestionResponse] = useState(null);
  const [showLanguageDropDown, setShowLanguageDropDown] = useState(false);
  const [selectedLanguageIndex, setSelectedLanguageIndex] = useState(0);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Ref so async callbacks always see the latest questionResponse without
  // needing it as a useCallback dependency (prevents stale closure bugs).
  const questionResponseRef = useRef(questionResponse);
  const selectedAttributes = Form.useWatch("attributes", form) ?? [];
  const selectedLanguages = Form.useWatch("languages", form) ?? [];

  // --- Effects ---
  useEffect(() => {
    fetchAttributes();
  }, []);

  useEffect(() => {
    questionResponseRef.current = questionResponse;
  }, [questionResponse]);

  useEffect(() => {
    if (!onboarding) return;

    if (!onboarding.questions) generateQuestions();

    if (onboarding.languages?.length) {
      const sorted = sortLanguagesEnFirst(onboarding.languages);
      setShowLanguageDropDown(true);
      form.setFieldValue("languages", [...new Set(sorted.map((l) => l.code))]);
    }

    setQuestionResponse(onboarding.questions ?? null);

    form.setFieldsValue({
      attributes: onboarding.attributes ?? [],
      run_at: onboarding.run_at ? dayjs(onboarding.run_at, DATE_FORMAT) : null,
    });

    if (onboarding.policy_file) {
      const parts = onboarding.policy_file.split("/");
      setPolicyFile({
        uid: "-1",
        name: parts[parts.length - 1],
        status: "done",
        url: `${DEFAULT_S3_FILE_BUCKET_URL}/${onboarding.policy_file}`,
      });
    }
  }, [onboarding]);

  // Derived from the form's `languages` field — no separate state needed.
  // English is always prepended and the list is kept sorted.
  const selectedLanguageList = useMemo(() => {
    if (!showLanguageDropDown || !selectedLanguages.length) return [];
    const withEn = selectedLanguages.includes("en")
      ? selectedLanguages
      : [...selectedLanguages, "en"];
    return sortLanguagesEnFirst(
      LANGUAGE_LISTS.filter((l) => withEn.includes(l.value)),
    );
  }, [showLanguageDropDown, selectedLanguages]);

  // --- Derived State ---

  const { surveyTitle, questionsList } = useMemo(() => {
    if (questionResponse?.surveys) {
      const code =
        selectedLanguageList[selectedLanguageIndex]?.code ??
        selectedLanguageList[selectedLanguageIndex]?.value ??
        "en";
      const survey = questionResponse.surveys.find(
        (s) => s.language?.code === code,
      );
      return {
        surveyTitle: survey?.title,
        questionsList: survey?.questions ?? [],
      };
    }
    return {
      surveyTitle: questionResponse?.title,
      questionsList: questionResponse?.questions ?? [],
    };
  }, [questionResponse, selectedLanguageList, selectedLanguageIndex]);

  const currentLanguage = useMemo(
    () => selectedLanguageList[selectedLanguageIndex]?.label ?? "English",
    [selectedLanguageList, selectedLanguageIndex],
  );

  const canManageDefinitions = selectedAttributes.length > 0;

  // True when the user has picked languages that differ from what was last saved/generated.
  // We compare against onboarding.languages (server state) rather than selectedLanguageList,
  // because selectedLanguageList is now fully derived from selectedLanguages — comparing them
  // would always be equal.
  const savedLanguageCodes = useMemo(
    () => (onboarding?.languages ?? []).map((l) => l.code ?? l.value),
    [onboarding?.languages],
  );
  const checkIfLanguagesChanged = !isArrayEqual(
    selectedLanguages,
    savedLanguageCodes,
  );

  const anyLoading = submitLoading || updateLoading || questionsLoading;

  // --- File Handling ---
  const handleFileChange = useCallback((file) => {
    if (!file) return;
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      message.error("You can only upload PDF files!");
      return Upload.LIST_IGNORE;
    }
    if (file.size / 1024 / 1024 > MAX_FILE_SIZE_MB) {
      message.error(`File must be smaller than ${MAX_FILE_SIZE_MB}MB!`);
      return Upload.LIST_IGNORE;
    }
    setPolicyFile(file);
    return false;
  }, []);

  // --- Data Fetchers ---
  const fetchAttributes = useCallback(async () => {
    try {
      const { data } = await onFetch({
        url: "/config/attributes/",
        method: "GET",
      });
      setAttributes(data ?? []);
    } catch {
      setAttributes([]);
    }
  }, [onFetch]);

  const generateQuestions = useCallback(async () => {
    try {
      const { data } = await onGenerateQuestion({
        url: `/company/onboarding/${onboarding.id}/questions/`,
        method: "GET",
      });
      setQuestionResponse(data);
      setSelectedLanguageIndex(0);
    } catch (err) {
      console.error("Error fetching questions:", err);
    }
  }, [onGenerateQuestion, onboarding?.id]);

  // Reads from ref so it never needs questionResponse as a dep —
  // safe to include in other useCallbacks without causing circular deps.
  const saveQuestions = useCallback(async () => {
    setError(null);
    const latest = questionResponseRef.current;

    // If multi-language is off and surveys exist, send only the English survey
    let payload = { ...latest };
    if (!showLanguageDropDown && (latest?.surveys ?? []).length > 0) {
      const englishSurvey = latest.surveys.find(
        (s) => s.language?.code === "en",
      );
      if (englishSurvey) {
        const { language: _omit, ...rest } = englishSurvey;
        payload = rest;
      }
    }

    try {
      await onUpdateQuestion({
        url: `/company/onboarding/${onboarding.id}/questions/`,
        method: "PUT",
        data: payload,
      });
    } catch {
      setError("Failed to save survey details. Please try again.");
    }
  }, [onUpdateQuestion, onboarding?.id, showLanguageDropDown]);

  // --- Form Submission ---
  const onFinish = useCallback(
    async (values) => {
      setError(null);
      try {
        const formData = new FormData();
        formData.append("multi_language", showLanguageDropDown ? 0 : 1);
        formData.append("regenerate", values?.regenerate === 0 ? 0 : 1);
        formData.append("run_at", values.run_at.toISOString());

        if (policyFile && !policyFile?.url) {
          formData.append("policy_file", policyFile);
        }

        const selectedLangs = values.languages ?? [];
        selectedLangs.forEach((lng) => formData.append("languages", lng));

        let policy_file_url = policyFile?.url ?? "";
        if (policy_file_url.startsWith(DEFAULT_S3_FILE_BUCKET_URL)) {
          policy_file_url = policy_file_url.replace(
            `${DEFAULT_S3_FILE_BUCKET_URL}/`,
            "",
          );
        }
        formData.append("policy_file_url", policy_file_url);
        values.attributes.forEach((id) => formData.append("attributes", id));

        const { data } = await onSubmit({
          url: `/company/onboarding/${onboarding.id}/regenerate/`,
          method: "POST",
          headers: { "Content-Type": "multipart/form-data" },
          data: formData,
        });

        setQuestionResponse(data);
        setSelectedLanguageIndex(0);

        if (showLanguageDropDown && selectedLangs.length) {
          // Always include English, then sync sorted codes back to the form field
          const langsWithEn = selectedLangs.includes("en")
            ? selectedLangs
            : [...selectedLangs, "en"];
          const sorted = sortLanguagesEnFirst(
            LANGUAGE_LISTS.filter((l) => langsWithEn.includes(l.value)),
          );
          form.setFieldValue("languages", [
            ...new Set(sorted.map((l) => l.value)),
          ]);
        } else {
          form.setFieldValue("languages", []);
        }
      } catch {
        setError("Failed to regenerate survey details. Please try again.");
      }
    },
    [policyFile, onSubmit, onboarding?.id, showLanguageDropDown, form],
  );

  // --- Question Mutations ---
  const onDelete = useCallback((id) => {
    setDeleteLoading(true);
    setQuestionResponse((prev) => {
      if (prev?.surveys) {
        return {
          ...prev,
          surveys: prev.surveys.map((s) => ({
            ...s,
            questions: s.questions.filter((q) => q.id !== id),
          })),
        };
      }
      return { ...prev, questions: prev.questions.filter((q) => q.id !== id) };
    });
    setDeleteLoading(false);
  }, []);

  const onQuestionRegenerate = useCallback(
    async (question) => {
      setRegenerateId(question.id);
      try {
        const { data } = await onReGenerateQuestion({
          url: `/company/onboarding/${onboarding.id}/regenerate-question/`,
          method: "POST",
          data: { question },
        });
        setQuestionResponse(data);
      } catch (err) {
        console.error("Error regenerating question:", err);
      } finally {
        setRegenerateId(null);
      }
    },
    [onReGenerateQuestion, onboarding?.id],
  );

  const updateQuestion = useCallback(
    async (values) => {
      // Capture the updated state synchronously inside the setter so the PUT
      // receives the fresh payload rather than the stale closure value.
      let updated;
      setQuestionResponse((prev) => {
        if (prev?.surveys) {
          const code =
            selectedLanguageList[selectedLanguageIndex]?.code ??
            selectedLanguageList[selectedLanguageIndex]?.value ??
            "en";
          updated = {
            ...prev,
            surveys: prev.surveys.map((survey) =>
              survey.language?.code !== code
                ? survey
                : {
                    ...survey,
                    questions: survey.questions.map((q) =>
                      q.id === values.id ? values : q,
                    ),
                  },
            ),
          };
        } else {
          updated = {
            ...prev,
            questions: prev.questions.map((q) =>
              q.id === values.id ? values : q,
            ),
          };
        }
        return updated;
      });

      try {
        await onUpdateQuestion({
          url: `/company/onboarding/${onboarding.id}/questions/`,
          method: "PUT",
          data: updated,
        });
      } catch {
        setError("Failed to save survey details. Please try again.");
      }
    },
    // questionResponse intentionally omitted — derived from functional setState above
    [
      selectedLanguageList,
      selectedLanguageIndex,
      onUpdateQuestion,
      onboarding?.id,
    ],
  );

  const onChangeLanguage = useCallback(async () => {
    const nextIndex = selectedLanguageIndex + 1;
    if (nextIndex >= selectedLanguageList.length) {
      await saveQuestions();
      onChangeStep();
    } else {
      setSelectedLanguageIndex(nextIndex);
    }
  }, [
    selectedLanguageIndex,
    selectedLanguageList.length,
    saveQuestions,
    onChangeStep,
  ]);

  // --- Handlers ---
  const handleSaveLanguage = useCallback(() => {
    form
      .validateFields()
      .then(() => onFinish({ ...form.getFieldsValue(), regenerate: 0 }))
      .catch(() => {});
  }, [form, onFinish]);

  const handleNext = useCallback(async () => {
    try {
      await form.validateFields();
      await onFinish({ ...form.getFieldsValue(), regenerate: 0 });
      await saveQuestions();
      onChangeStep();
    } catch (err) {
      console.error(err);
    }
  }, [form, onFinish, saveQuestions, onChangeStep]);

  const handleApproveLanguage = useCallback(() => {
    modal.confirm({
      title: `You're approving the ${currentLanguage} language questions`,
      icon: <ExclamationCircleFilled />,
      content: (
        <span>
          By clicking Approve below, you will be confirming the approval of the{" "}
          <b>{currentLanguage}</b> language questions. Once approved, you can
          always go back and change the questions in the admin dashboard.
        </span>
      ),
      okText: "Approve",
      okType: "primary",
      cancelText: "Cancel",
      centered: true,
      destroyOnClose: true,
      onOk: onChangeLanguage,
    });
  }, [modal, currentLanguage, onChangeLanguage]);

  const confirmDelete = useCallback(
    (question) => {
      modal.confirm({
        centered: true,
        icon: <ExclamationCircleOutlined />,
        style: { borderRadius: 7 },
        okButtonProps: { loading: deleteLoading },
        title: "Are you sure you want to delete this question?",
        content: (
          <>
            <ModalContentTile>{question.text}</ModalContentTile>
            <p>This action cannot be undone.</p>
          </>
        ),
        okText: "Confirm",
        cancelText: "Cancel",
        destroyOnClose: true,
        onOk: () => onDelete(question.id),
      });
    },
    [modal, deleteLoading, onDelete],
  );

  const handlePrevious = useCallback(() => {
    if (selectedLanguageIndex === 0) {
      onPreviousStep();
    } else {
      setSelectedLanguageIndex((i) => i - 1);
    }
  }, [selectedLanguageIndex, onPreviousStep]);

  // --- Render ---
  if (loading) return <FullPageLoader />;

  return (
    <>
      <Container>
        <LeftWrapper>
          <Wrapper>
            <h3 style={{ fontSize: 24 }}>
              In this section, our AI Organizational Psychologist intelligently
              generates scientifically grounded micro-surveys tailored to your
              team's real-time needs.
            </h3>

            {error && (
              <Alert
                message={error}
                type="error"
                showIcon
                style={{ marginBottom: 20 }}
              />
            )}

            <Flex
              justify="space-between"
              align="center"
              style={{ marginBottom: 7 }}
            >
              <span style={{ fontWeight: 600, fontSize: 16 }}>Core Values</span>
              <span
                style={{
                  color: canManageDefinitions
                    ? colorPalette.primary[500]
                    : "rgba(12,12,12,0.88)",
                  cursor: canManageDefinitions ? "pointer" : "default",
                }}
                onClick={() =>
                  canManageDefinitions && setShowAttributesModal(true)
                }
              >
                Manage Definition
              </span>
            </Flex>

            <Form layout="vertical" form={form} onFinish={onFinish}>
              <FormItem
                name="attributes"
                rules={[{ required: true, message: "This field is required" }]}
              >
                <Select
                  placeholder="Select Core Values"
                  options={attributes}
                  mode="tags"
                />
              </FormItem>

              <FormItem
                label="HR Policy"
                tooltip={{
                  title:
                    "You can upload your HR policy at any stage. Adding it now helps improve recommendations.",
                  icon: (
                    <QuestionCircleTwoTone
                      twoToneColor={colorPalette.primary[500]}
                      style={{ marginLeft: 8, fontSize: 16 }}
                    />
                  ),
                }}
              >
                <Upload
                  accept=".pdf"
                  beforeUpload={handleFileChange}
                  fileList={policyFile ? [policyFile] : []}
                  onRemove={() => setPolicyFile(null)}
                >
                  <Button type="primary" ghost icon={<UploadOutlined />}>
                    {policyFile
                      ? "Click Here to Update"
                      : "Click Here to Upload"}
                  </Button>
                </Upload>
              </FormItem>

              <FormItem
                name="run_at"
                label="First survey date and time"
                rules={[{ required: true, message: "This field is required" }]}
                help="This is when the first survey will be sent out. You can change this later in the dashboard."
              >
                <DatePicker
                  showTime={{ use12Hours: true }}
                  minDate={dayjs()}
                  placeholder="Select Date"
                  format={DATE_FORMAT}
                  style={{ width: "100%" }}
                  allowClear={false}
                />
              </FormItem>

              <SwitchWrapper>
                <span>Do you want to send surveys in multiple languages?</span>
                <Switch
                  value={showLanguageDropDown}
                  onChange={(value) => {
                    setShowLanguageDropDown(value);
                    if (!value) form.setFieldValue("languages", []);
                  }}
                />
              </SwitchWrapper>

              {showLanguageDropDown && (
                <FormItem
                  name="languages"
                  label="Select your languages"
                  rules={[
                    { required: true, message: "This field is required" },
                  ]}
                  help={
                    <span>
                      Employees receive the option to select their preferred
                      language on their first survey. Use the{" "}
                      <a onClick={() => setShowPreviewModal(true)}>preview</a>{" "}
                      to see this experience before sending.
                    </span>
                  }
                >
                  <Select
                    placeholder="Select Languages"
                    options={LANGUAGE_LISTS}
                    allowClear
                    showSearch
                    optionFilterProp="title"
                    mode="multiple"
                  />
                </FormItem>
              )}
            </Form>
          </Wrapper>
        </LeftWrapper>

        <RightWrapper>
          {submitLoading || questionsLoading ? (
            <AnimatedLoader
              fullPage
              languageLoader={selectedLanguages.length > 0}
            />
          ) : (
            <QuestionContainer>
              <Flex align="center" justify="space-between" gap={20}>
                <SurveyTitle>{surveyTitle}</SurveyTitle>
                <Button
                  ghost
                  type="primary"
                  loading={submitLoading}
                  onClick={generateQuestions}
                >
                  Regenerate All Questions
                </Button>
              </Flex>

              {questionsList.map((question, index) =>
                question.id === regenerateId ? (
                  <div key={question.id}>
                    <AnimatedLoader intervalSeconds={2000} />
                    <Divider />
                  </div>
                ) : (
                  <Questions
                    key={question.id}
                    question={question}
                    index={index}
                    onRegenerate={() => onQuestionRegenerate(question)}
                    onUpdateQuestion={updateQuestion}
                    onDelete={() => confirmDelete(question)}
                  />
                ),
              )}
            </QuestionContainer>
          )}
        </RightWrapper>

        <FooterWrapper>
          <Flex align="center" justify="space-between" gap={10}>
            <NextButton
              size="large"
              type="primary"
              ghost
              loading={anyLoading}
              onClick={handlePrevious}
            >
              Previous
            </NextButton>

            {!showLanguageDropDown || selectedLanguages.length === 0 ? (
              <NextButton
                size="large"
                type="primary"
                loading={anyLoading}
                onClick={handleNext}
              >
                Save & Next
              </NextButton>
            ) : checkIfLanguagesChanged ? (
              <NextButton
                size="large"
                type="primary"
                loading={anyLoading}
                onClick={handleSaveLanguage}
              >
                Save Languages
              </NextButton>
            ) : (
              <Button
                size="large"
                type="primary"
                loading={updateLoading}
                onClick={handleApproveLanguage}
              >
                Approve {currentLanguage} Questions
              </Button>
            )}
          </Flex>
        </FooterWrapper>
      </Container>

      <AttributesDefinitionModal
        onboarding={onboarding}
        open={showAttributesModal}
        onClose={() => setShowAttributesModal(false)}
        attributes={attributes}
        selectedAttributes={form.getFieldValue("attributes") ?? []}
      />

      <PreviewModal
        title="Language Selection Preview"
        open={showPreviewModal}
        footer={null}
        width="95%"
        onCancel={() => setShowPreviewModal(false)}
        destroyOnHidden
        style={{ top: 20 }}
      >
        <img
          style={{ width: "70%", height: "auto" }}
          src={lngPreviewImage}
          alt="language preview"
        />
      </PreviewModal>

      {contextHolder}
    </>
  );
};

export default SurveyPreview;
